import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Monitor, Smartphone, Image as ImageIcon, AlertTriangle, Code, Eye, Info,
} from 'lucide-react';
import { apiHtmlPreview, apiUploadEmailImage } from '../../api';
import styles from './Mailer.module.css';

/**
 * Paste HTML, see exactly what will be sent.
 *
 * Two things make this more than a textarea beside an iframe:
 *
 * 1. The preview is rendered from the *server's* output, not the raw paste.
 *    Those differ in the ways that matter — the browser will happily show a
 *    `data:` image that Gmail refuses to display, and will render a `<script>`
 *    tag's effects that never survive the sanitiser. Previewing the raw paste
 *    would show a message that no recipient will ever see.
 *
 * 2. It runs in a sandboxed iframe. Pasted marketing HTML routinely carries a
 *    `<style>` block full of bare element selectors — `table { width: 100% }`,
 *    `p { margin: 0 }` — and rendering that inline would repaint the admin UI
 *    around it. The iframe is also the honest preview: a mail client renders
 *    the message in its own document, not inside yours.
 */
const HtmlBodyEditor = ({ jwtToken, value, onChange, coldOutreach = true }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState('split');
  const [width, setWidth] = useState('desktop');
  const [uploadError, setUploadError] = useState('');
  const fileInput = useRef(null);
  const textarea = useRef(null);

  // Debounced so a long template is not re-sanitised on every keystroke, but
  // short enough that pasting feels immediate.
  useEffect(() => {
    if (!value?.trim()) { setPreview(null); return undefined; }
    const timer = setTimeout(async () => {
      setBusy(true);
      try {
        setPreview(await apiHtmlPreview(jwtToken, { html: value, coldOutreach }));
      } catch (e) {
        setPreview({ error: e.message });
      } finally {
        setBusy(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [value, jwtToken, coldOutreach]);

  /** Insert text at the cursor rather than appending, so an <img> lands where it was wanted. */
  const insertAtCursor = useCallback((snippet) => {
    const el = textarea.current;
    if (!el) { onChange(`${value || ''}\n${snippet}`); return; }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = `${value.slice(0, start)}${snippet}${value.slice(end)}`;
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + snippet.length;
    });
  }, [value, onChange]);

  const upload = async (file) => {
    if (!file) return;
    setUploadError('');
    try {
      const { url } = await apiUploadEmailImage(jwtToken, file);
      insertAtCursor(`<img src="${url}" alt="" style="max-width:100%;height:auto;display:block">`);
    } catch (e) {
      setUploadError(e.message);
    }
  };

  const onDrop = (event) => {
    event.preventDefault();
    const file = [...(event.dataTransfer?.files || [])].find((f) => f.type.startsWith('image/'));
    if (file) upload(file);
  };

  const problems = preview?.problems || [];
  const advice = preview?.advice || [];
  const sizeKb = preview?.sizeBytes ? Math.round(preview.sizeBytes / 1024) : 0;

  const showEditor = view !== 'preview';
  const showPreview = view !== 'code';

  return (
    <div className={styles.htmlEditor}>
      <div className={styles.htmlToolbar}>
        <div className={styles.segmented}>
          {[
            { key: 'code', label: 'HTML', icon: Code },
            { key: 'split', label: 'Split', icon: null },
            { key: 'preview', label: 'Preview', icon: Eye },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              className={`${styles.segment} ${view === key ? styles.segmentActive : ''}`}
              onClick={() => setView(key)}
            >
              {Icon && <Icon size={13} />} {label}
            </button>
          ))}
        </div>

        {showPreview && (
          <div className={styles.segmented}>
            <button
              type="button"
              className={`${styles.segment} ${width === 'desktop' ? styles.segmentActive : ''}`}
              onClick={() => setWidth('desktop')}
              title="Desktop width"
            >
              <Monitor size={13} />
            </button>
            <button
              type="button"
              className={`${styles.segment} ${width === 'mobile' ? styles.segmentActive : ''}`}
              onClick={() => setWidth('mobile')}
              title="Mobile width — most cold email is read on a phone"
            >
              <Smartphone size={13} />
            </button>
          </div>
        )}

        <div className={styles.spacer} />

        {sizeKb > 0 && (
          <span className={sizeKb > 100 ? styles.sizeWarn : styles.sizeOk}>{sizeKb} KB</span>
        )}
        {busy && <span className="spinner" style={{ width: 14, height: 14 }} />}

        <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileInput.current?.click()}>
          <ImageIcon size={14} /> Add an image
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          hidden
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }}
        />
      </div>

      {uploadError && <div className={styles.dangerBox}>{uploadError}</div>}

      <div className={view === 'split' ? styles.htmlSplit : undefined}>
        {showEditor && (
          <textarea
            ref={textarea}
            className={`input ${styles.htmlSource}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            spellCheck={false}
            placeholder={'<div style="font-family:Arial,sans-serif;max-width:600px">\n  <h1>Your headline</h1>\n  <p>Paste the HTML your design tool produced, or write it here.</p>\n</div>\n\nDrag an image in and it gets hosted automatically.'}
          />
        )}

        {showPreview && (
          <div className={styles.htmlPreviewPane}>
            {preview?.error ? (
              <div className={styles.dangerBox}>{preview.error}</div>
            ) : preview?.html ? (
              <iframe
                title="Email preview"
                className={`${styles.htmlPreviewFrame} ${width === 'mobile' ? styles.htmlPreviewMobile : ''}`}
                // Sandboxed with no allow-scripts: the preview must not be able
                // to run anything, and neither can the real message — mail
                // clients strip scripts too, so a scriptless preview is the
                // accurate one.
                sandbox=""
                srcDoc={`<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;padding:16px;font-family:-apple-system,Segoe UI,Roboto,sans-serif;background:#fff;color:#111}img{max-width:100%}</style></head><body>${preview.html}</body></html>`}
              />
            ) : (
              <div className={styles.htmlPreviewEmpty}>
                {value?.trim() ? 'Rendering…' : 'Paste some HTML to see it here.'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Problems break for the recipient and must be fixed. Advice costs
          deliverability and is a judgement call. Never merged into one list. */}
      {problems.length > 0 && (
        <div className={styles.dangerBox}>
          <div className={styles.boxTitle}><AlertTriangle size={16} /> These will break for recipients</div>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {problems.map((p) => <li key={p}>{p}</li>)}
          </ul>
        </div>
      )}

      {advice.length > 0 && (
        <div className={styles.warnBox}>
          <div className={styles.boxTitle}><Info size={16} /> Worth knowing before you send</div>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {advice.map((a) => <li key={a.text}>{a.text}</li>)}
          </ul>
        </div>
      )}

      {preview && preview.storageConfigured === false && (
        <div className={styles.warnBox}>
          <div className={styles.boxTitle}><AlertTriangle size={16} /> Image hosting is not set up</div>
          File storage is not configured on the server, so inline images cannot be hosted and will
          arrive broken. Either configure R2 storage, or use images already on a public URL.
        </div>
      )}

      <div className={styles.mergeHint}>
        <strong>Merge tags work here too:</strong> <code>{'{{firstName|there}}'}</code>{' '}
        <code>{'{{company}}'}</code> — anywhere in the markup, including inside attributes.
        <br />
        <strong>Images:</strong> drag one in, or use the button. Pasted <code>data:</code> images are
        uploaded and re-linked automatically, because Gmail refuses to render them inline.
      </div>
    </div>
  );
};

export default HtmlBodyEditor;
